import { useState } from "react";

import { Input } from "@/components/ui/input";
import { CheckCircleIcon, MagnifyingGlassIcon } from "@phosphor-icons/react";

/**
 * Searchable checklist over arbitrary records.
 *
 * PeopleMultiSelect is the same idea bound to api.js's user shape; this one
 * takes plain `{ id, label, hint }` items so it can select initiatives,
 * projects, or anything else. Kept separate rather than merged so the pages
 * already using PeopleMultiSelect are not disturbed.
 */
export default function MultiSelect({
  items = [],
  value = [],
  onChange,
  placeholder = "Search…",
  emptyMessage = "Nothing to choose from.",
  testId = "multi-select",
  maxHeight = "max-h-40",
}) {
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  const matches = q
    ? items.filter((i) => `${i.label || ""} ${i.hint || ""}`.toLowerCase().includes(q))
    : items;

  const toggle = (id) => {
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  };

  return (
    <div>
      {items.length > 3 && (
        <div className="relative mb-2">
          <MagnifyingGlassIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={placeholder}
            data-testid={`${testId}-search`}
            className="rounded-sm h-8 pl-8 text-xs"
          />
        </div>
      )}
      <div className={`border border-border rounded-sm ${maxHeight} overflow-y-auto divide-y divide-border`} data-testid={testId}>
        {items.length === 0 && (
          <div className="px-3 py-3 text-[11px] text-zinc-500" data-testid={`${testId}-empty`}>{emptyMessage}</div>
        )}
        {items.length > 0 && matches.length === 0 && (
          <div className="px-3 py-3 text-[11px] text-zinc-500" data-testid={`${testId}-no-match`}>
            Nothing matches “{query.trim()}”.
          </div>
        )}
        {matches.map((i) => {
          const checked = value.includes(i.id);
          return (
            <button
              type="button"
              key={i.id}
              onClick={() => toggle(i.id)}
              data-testid={`${testId}-option-${i.id}`}
              className={`w-full flex items-center justify-between gap-2 px-3 py-1.5 text-left text-xs transition-colors ${checked ? "bg-[#EFF6FF]" : "hover:bg-zinc-50"}`}
            >
              <span className="min-w-0 truncate">
                {i.label}
                {i.hint && <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 ml-1.5">{i.hint}</span>}
              </span>
              {checked && <CheckCircleIcon size={14} weight="fill" color="#0176D3" className="shrink-0" />}
            </button>
          );
        })}
      </div>
    </div>
  );
}
